/**
 * The tenant platform's module catalogue keys, mirrored here as a CLOSED
 * vocabulary.
 *
 * WHY THIS FILE EXISTS. A hospital signup carries the list of modules the
 * prospect ticked, and those keys are the tenant's (`hospital-HRMS`
 * `backend/src/constants/module-catalog.ts`), not Control's. Control's own
 * MODULE_KEYS in commercial.ts are the eight COMMERCIAL bundles a contract is
 * priced on — a different vocabulary for a different purpose, and mapping one
 * onto the other at intake would throw away what the prospect actually asked
 * for. So the raw keys are carried, and this list is what makes that safe.
 *
 * WHY A LIST AND NOT `z.string()`. Two reasons, and the second is the one that
 * forced this file into existence.
 *
 * 1. Deny by default. An open string field on a public-facing boundary accepts
 *    anything; a closed enum accepts 50 known words. The signup event is
 *    attacker-reachable in the sense that anybody can fill in the tenant's
 *    public signup form, so the narrower the shape, the better.
 *
 * 2. The prohibited-content scanner rejects three of these keys. Measured
 *    against the real scanner before any of this was wired:
 *
 *      findProhibitedContent({ requestedModules: ['patients'] })
 *        -> [{ rule: 'clinical-text', path: 'requestedModules[0]' }]
 *      ... same for 'prescriptions', and for Control's own 'patient_portal'.
 *
 *    That is the `clinical-text` VALUE rule doing exactly its job: it is a
 *    free-text scan, and the free text "patients" has no business crossing this
 *    boundary. The word as a catalogue KEY does. Without this list the first
 *    real signup event — every signup ticks `patients`, it is the core module —
 *    would have been rejected 400 at the boundary, audited as prohibited
 *    content, and the pipeline would have looked wired and delivered nothing.
 *
 *    The exemption is therefore membership of THIS list, matched exactly, and
 *    nothing else: see ALLOWED_VOCABULARY_VALUES in prohibited.ts. "patients"
 *    passes; "patients admitted today" does not, "patient_name" does not, and a
 *    key named `patientName` is still caught by the key rules regardless of its
 *    value. Relaxing the rule itself would have been the wrong fix — it would
 *    have opened every string field on every boundary to clinical prose.
 *
 * DRIFT. This duplicates a list that lives in another repository, for the same
 * reason migrations 0021 and 0023 duplicate PROVISIONING_ACTIONS into a CHECK
 * constraint: the other side cannot be imported. A module added on the tenant
 * and not added here is REJECTED at intake rather than silently accepted, which
 * is the correct direction to fail — a rejected event is visible in the audit
 * log; a silently-widened boundary is not. tenant-modules.test.ts asserts the
 * count so a drift is a test failure and not a surprise in production.
 */
export const TENANT_MODULE_KEYS = [
    // Clinical core
    'patients',
    'appointments',
    'opd',
    'prescriptions',
    'basicBilling',
    'basicReports',
    'labOrders',
    'radiologyOrders',
    'ipd',
    'emergency',
    'operationTheatre',
    'dietetics',
    'referrals',
    'telemedicine',
    'infectionControl',
    'clinicalForms',
    // Specialist clinical
    'dialysis',
    'physiotherapy',
    'endoscopy',
    'cathlab',
    'chemotherapy',
    'radiationOncology',
    'bloodBank',
    'transplant',
    'apheresis',
    'ivf',
    'prosthetics',
    'clinicalTrials',
    'biobank',
    // Operations
    'pharmacy',
    'inventory',
    'assetManagement',
    'insuranceClaims',
    'walkinTokens',
    'multiLocation',
    'patientPortal',
    // People
    'hrms',
    'attendance',
    'leaveManagement',
    'payroll',
    'performanceReviews',
    // Insight and platform
    'advancedReports',
    'advancedAnalytics',
    'commandCentre',
    'apiAccess',
    'customIntegrations',
    // Found only by EXECUTING the tenant's ALL_MODULE_KEYS (it is computed at
    // runtime from BAND_CONFIG.enterprise.modules, so no grep can enumerate it).
    // A static read of module-catalog.ts missed this one and produced a list of
    // 50, which is why the count assertion below is load-bearing.
    'ai_assistant',
    'smsNotifications',
    'whiteLabel',
    'prioritySupport',
    'abdmHealthId',
];
/**
 * Human labels and grouping for the operator's module screen.
 *
 * The keys are the tenant's wire vocabulary and are deliberately NOT renamed
 * anywhere in transit. But an operator ticking boxes on behalf of a hospital
 * should not have to know that "operationTheatre" is theatre scheduling or
 * that "abdmHealthId" is the national health id integration. The label is a
 * presentation concern and lives here, next to the keys, so that adding a
 * module without labelling it fails the test below rather than silently
 * rendering a raw camelCase key to a paying customer.
 */
export const TENANT_MODULE_GROUPS = [
    'Clinical core',
    'Specialist clinical',
    'Operations',
    'People',
    'Insight and platform',
];
export const TENANT_MODULE_CATALOGUE = [
    { key: 'patients', label: 'Patient records', group: 'Clinical core' },
    { key: 'appointments', label: 'Appointments', group: 'Clinical core' },
    { key: 'opd', label: 'Outpatient (OPD)', group: 'Clinical core' },
    { key: 'prescriptions', label: 'Prescriptions', group: 'Clinical core' },
    { key: 'basicBilling', label: 'Billing', group: 'Clinical core' },
    { key: 'basicReports', label: 'Standard reports', group: 'Clinical core' },
    { key: 'labOrders', label: 'Laboratory orders', group: 'Clinical core' },
    { key: 'radiologyOrders', label: 'Radiology orders', group: 'Clinical core' },
    { key: 'ipd', label: 'Inpatient (IPD)', group: 'Clinical core' },
    { key: 'emergency', label: 'Emergency department', group: 'Clinical core' },
    { key: 'operationTheatre', label: 'Operation theatre', group: 'Clinical core' },
    { key: 'dietetics', label: 'Dietetics', group: 'Clinical core' },
    { key: 'referrals', label: 'Referrals', group: 'Clinical core' },
    { key: 'telemedicine', label: 'Telemedicine', group: 'Clinical core' },
    { key: 'infectionControl', label: 'Infection control', group: 'Clinical core' },
    { key: 'clinicalForms', label: 'Clinical forms', group: 'Clinical core' },
    { key: 'dialysis', label: 'Dialysis', group: 'Specialist clinical' },
    { key: 'physiotherapy', label: 'Physiotherapy', group: 'Specialist clinical' },
    { key: 'endoscopy', label: 'Endoscopy', group: 'Specialist clinical' },
    { key: 'cathlab', label: 'Cath lab', group: 'Specialist clinical' },
    { key: 'chemotherapy', label: 'Chemotherapy', group: 'Specialist clinical' },
    { key: 'radiationOncology', label: 'Radiation oncology', group: 'Specialist clinical' },
    { key: 'bloodBank', label: 'Blood bank', group: 'Specialist clinical' },
    { key: 'transplant', label: 'Transplant', group: 'Specialist clinical' },
    { key: 'apheresis', label: 'Apheresis', group: 'Specialist clinical' },
    { key: 'ivf', label: 'IVF', group: 'Specialist clinical' },
    { key: 'prosthetics', label: 'Prosthetics', group: 'Specialist clinical' },
    { key: 'clinicalTrials', label: 'Clinical trials', group: 'Specialist clinical' },
    { key: 'biobank', label: 'Biobank', group: 'Specialist clinical' },
    { key: 'pharmacy', label: 'Pharmacy', group: 'Operations' },
    { key: 'inventory', label: 'Inventory', group: 'Operations' },
    { key: 'assetManagement', label: 'Asset management', group: 'Operations' },
    { key: 'insuranceClaims', label: 'Insurance claims', group: 'Operations' },
    { key: 'walkinTokens', label: 'Walk-in tokens', group: 'Operations' },
    { key: 'multiLocation', label: 'Multiple locations', group: 'Operations' },
    { key: 'patientPortal', label: 'Patient portal', group: 'Operations' },
    { key: 'hrms', label: 'Staff records (HRMS)', group: 'People' },
    { key: 'attendance', label: 'Attendance', group: 'People' },
    { key: 'leaveManagement', label: 'Leave management', group: 'People' },
    { key: 'payroll', label: 'Payroll', group: 'People' },
    { key: 'performanceReviews', label: 'Performance reviews', group: 'People' },
    { key: 'advancedReports', label: 'Advanced reports', group: 'Insight and platform' },
    { key: 'advancedAnalytics', label: 'Advanced analytics', group: 'Insight and platform' },
    { key: 'commandCentre', label: 'Command centre', group: 'Insight and platform' },
    { key: 'apiAccess', label: 'API access', group: 'Insight and platform' },
    { key: 'customIntegrations', label: 'Custom integrations', group: 'Insight and platform' },
    { key: 'ai_assistant', label: 'AI assistant', group: 'Insight and platform' },
    { key: 'smsNotifications', label: 'SMS notifications', group: 'Insight and platform' },
    { key: 'whiteLabel', label: 'White labelling', group: 'Insight and platform' },
    { key: 'prioritySupport', label: 'Priority support', group: 'Insight and platform' },
    { key: 'abdmHealthId', label: 'ABDM health id', group: 'Insight and platform' },
];
/** Label for a key, falling back to the key itself rather than throwing. */
export function tenantModuleLabel(key) {
    return TENANT_MODULE_CATALOGUE.find((m) => m.key === key)?.label ?? key;
}
/**
 * Measured on the tenant's shipped catalogue, 2026-09-15, by RUNNING
 * `ALL_MODULE_KEYS.length` rather than by grepping the source:
 *
 *   $ npx tsx -e "import {ALL_MODULE_KEYS} from './src/config/entitlements.config'; ..."
 *   COUNT=51
 *
 * The distinction matters. `ALL_MODULE_KEYS` is `Object.keys(
 * BAND_CONFIG.enterprise.modules)` — computed at runtime, so a static read of
 * module-catalog.ts cannot enumerate it and silently returned 50, omitting
 * `ai_assistant`. See DRIFT above: a missing key is a REJECTED event, so this
 * count assertion is what converts a silent drift into a test failure.
 */
export const TENANT_MODULE_KEY_COUNT = 51;
const TENANT_MODULE_KEY_SET = new Set(TENANT_MODULE_KEYS);
export function isTenantModuleKey(value) {
    return TENANT_MODULE_KEY_SET.has(value);
}
//# sourceMappingURL=tenant-modules.js.map